import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import AccountInfo from './AccountInfo';
import { FaSignInAlt, FaSignOutAlt, FaArrowLeft, FaHistory } from 'react-icons/fa';
import { useAuth } from './AuthContext';      // <-- 1. Import useAuth
import axiosInstance from './api/axios';   // <-- 2. Import axiosInstance

// Helper function (remains the same)
const parseLogEntry = (description) => {
  const isLogin = description.toLowerCase().includes('logged in');
  const isLogout = description.toLowerCase().includes('logged out');
  let icon = <FaHistory />, action = "Activity", colorClass = "default";
  if (isLogin) { icon = <FaSignInAlt />; action = "Login"; colorClass = "login"; }
  else if (isLogout) { icon = <FaSignOutAlt />; action = "Logout"; colorClass = "logout"; }
  return { icon, action, colorClass, details: description };
};

export default function LoginHistory() {
  const [logDescriptions, setLogDescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // --- 3. Get user object from our central context ---
  const { user } = useAuth(); 
  const navigate = useNavigate();

  // --- 4. Refactored data fetching logic ---
  const fetchLogs = useCallback(async () => {
    if (!user) return; // Don't fetch if user data isn't loaded yet

    setLoading(true);
    setError(null);
    try {
      // Use axiosInstance and our new secure endpoint
      const response = await axiosInstance.get('/api/user/login-history');
      setLogDescriptions(response.data);
      setLoading(false); // Stop loading on success
    } catch (err) {
      // --- TIMEOUT FIX: Prevent error flash on timeout ---
      if (err.response?.status === 401) return;
      
      setError(err.response?.data?.detail || "Failed to load login history.");
      setLoading(false); // Stop loading on actual errors
    }
  }, [user]); // Re-run if the user object changes

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="history-page-container">
      <header className="dashboard-header">
        <div className="logo-section">
          <img src={logo} className="logo-icon" alt="Wysupp Logo" />
          <span className="logo-text">Wysupp Validate</span>
          <span className="logo-subtext">Data Quality Management</span>
        </div>
        <AccountInfo />
      </header>

      <main className="history-main-content">
        <div className="page-header">
          <button onClick={() => navigate('/home')} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <h1 className="page-title">Login History</h1>
        </div>

        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your login history...</p>
          </div>
        )}

        {error && <p className="error-state">{error}</p>}

        {!loading && !error && (
          <div className="logs-list">
            {logDescriptions.length === 0 ? (
              <p className="info-state">No login or logout activity has been recorded for '{user?.username}'.</p>
            ) : (
              logDescriptions.map((description, index) => {
                const { icon, colorClass, details } = parseLogEntry(description);
                return (
                  <div key={index} className={`log-card ${colorClass}`}>
                    <div className={`log-card-icon ${colorClass}`}>
                      {icon}
                    </div>
                    <div className="log-card-info">
                      <p className="log-details">{details}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>
      <style>{`
                :root {
                    --dark-bg: #1a202c;
                    --card-bg: #2d3748;
                    --text-color: #e2e8f0;
                    --light-text: #a0aec0;
                    --green-accent: #48bb78;
                    --border-color: #4a5568;
                    --error-color: #ef4444;
                    --error-bg: rgba(239, 68, 68, 0.1);
                }

                .history-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                    /* Add these two lines */
                    display: flex;
                    flex-direction: column;
                }

                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }

                .history-main-content {
                    max-width: 900px;
                    margin: 2rem auto;
                    padding: 0 2rem;
                    width: 100%; /* Ensure it takes full width within its constraints */
                    /* Add this line */
                    flex-grow: 1;
                }

                .page-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .back-button {
                    background: var(--card-bg);
                    color: var(--text-color);
                    border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .back-button:hover {
                    background: #4a5568;
                }

                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                }

                .loading-state, .error-state, .info-state {
                    text-align: center;
                    padding: 3rem;
                    background-color: var(--card-bg);
                    border-radius: 8px;
                    color: var(--light-text);
                }
                .error-state { color: var(--error-color); background-color: var(--error-bg); font-weight: 500; }
                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    margin: 0 auto 1rem auto;
                    border: 4px solid var(--border-color);
                    border-top-color: var(--green-accent);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                .logs-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .log-card {
                    background-color: var(--card-bg);
                    border-radius: 8px;
                    padding: 1rem 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    border-left: 4px solid var(--border-color);
                }
                .log-card.login {
                    border-left-color: var(--green-accent);
                }
                .log-card.logout {
                    border-left-color: #f59e0b; /* Orange for logout */
                }

                .log-card-icon {
                    font-size: 1.5rem;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .log-card-icon.login {
                    background-color: rgba(72, 187, 120, 0.1);
                    color: var(--green-accent);
                }
                .log-card-icon.logout {
                    background-color: rgba(245, 158, 11, 0.1);
                    color: #f59e0b;
                }
                .log-card-icon.default {
                     background-color: rgba(107, 114, 128, 0.1);
                     color: var(--light-text);
                }

                .log-card-info {
                    flex-grow: 1;
                }
                
                .log-details {
                    font-size: 0.95rem;
                    color: var(--text-color);
                    margin: 0;
                    line-height: 1.5;
                }
                .logo-subtext {
                    font-size: 0.75rem;
                    color: var(--light-text);
                    margin-left: 0.5rem;
                    padding-left: 0.5rem;
                    border-left: 1px solid var(--light-text);
                }
            `}</style>
    </div>
  );
}